import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Home, Search, PlusCircle, MapPin } from 'lucide-react';

const NotFoundPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const t = (fr, en) => (language === 'fr' ? fr : en);

  return (
    <div className="min-h-[80vh] py-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Hero */}
        <div className="text-center mb-12">
          <div
            className="inline-block mb-6 text-[120px] md:text-[180px] font-bold leading-none tracking-tighter bg-gradient-to-br from-primary to-blue-400 bg-clip-text text-transparent select-none"
            aria-hidden="true"
            data-testid="404-illustration"
          >
            4<span className="inline-block mx-2 animate-pulse">·</span>4
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-3" data-testid="404-title">
            {t('Toujours à la recherche du bon logement\u00a0?', 'Still looking for the right home?')}
          </h1>
          <p className="text-muted-foreground text-base md:text-lg max-w-xl mx-auto">
            {t(
              "Cette page n'existe pas ou a été déplacée. Revenons sur le bon chemin — on a peut-être déjà votre future maison.",
              "This page doesn't exist or was moved. Let's get you back on track — your next home might already be waiting."
            )}
          </p>
        </div>

        {/* CTA buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-12">
          <Button onClick={() => navigate('/')} size="lg" className="gap-2 rounded-full" data-testid="404-home-btn">
            <Home className="h-5 w-5" />
            {t("Retour à l'accueil", 'Back to home')}
          </Button>
          <Button onClick={() => navigate('/listings')} size="lg" variant="outline" className="gap-2 rounded-full" data-testid="404-listings-btn">
            <Search className="h-5 w-5" />
            {t('Parcourir les logements', 'Browse listings')}
          </Button>
        </div>

        {/* Suggestion cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            {
              to: '/listings?sort=featured',
              icon: MapPin,
              title: t('Annonces en vedette', 'Featured listings'),
              desc: t('Les coups de cœur du moment', "This week's favorites"),
              testid: '404-card-featured',
            },
            {
              to: '/listings?city=city_yaounde',
              icon: Search,
              title: t('Logements à Yaoundé', 'Stays in Yaoundé'),
              desc: t('Bastos, Bonapriso, Akwa...', 'Bastos, Bonapriso, Akwa…'),
              testid: '404-card-city',
            },
            {
              to: '/dashboard/new',
              icon: PlusCircle,
              title: t('Publier un logement', 'List your place'),
              desc: t('Mettez votre bien en location en quelques minutes', 'Rent out your property in minutes'),
              testid: '404-card-publish',
            },
          ].map(({ to, icon: Icon, title, desc, testid }) => (
            <Link key={to} to={to} data-testid={testid}>
              <Card className="h-full hover:shadow-lg hover:-translate-y-1 transition-all border-border/60">
                <CardContent className="p-5 space-y-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold">{title}</h3>
                  <p className="text-sm text-muted-foreground">{desc}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;
